import { Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorType } from '@core/models/app-error.model';
import { TranslationKey } from '@core/utils/marker';

const ERROR_TRANSLATION_KEYS: Record<ErrorType, TranslationKey> = {
  unauthorized: 'ERROR.UNAUTHORIZED',
  forbidden: 'ERROR.FORBIDDEN',
  'not-found': 'ERROR.NOT_FOUND',
  'bad-request': 'ERROR.BAD_REQUEST',
  'server-error': 'ERROR.SERVER_ERROR',
  network: 'ERROR.NETWORK',
  unknown: 'ERROR.UNKNOWN',
};

@Injectable({ providedIn: 'root' })
export class ErrorHandlerService {
  readonly visible = signal(false);
  readonly errorType = signal<ErrorType>('unknown');
  readonly translationKey = signal<TranslationKey>('ERROR.UNKNOWN');
  readonly requestId = signal<string | null>(null);

  handle(error: unknown, requestId?: string | null): void {
    const type = this.categorize(error);
    this.errorType.set(type);
    this.translationKey.set(ERROR_TRANSLATION_KEYS[type]);
    this.requestId.set(requestId ?? null);
    this.visible.set(true);
  }

  show(translationKey: TranslationKey): void {
    this.errorType.set('unknown');
    this.translationKey.set(translationKey);
    this.requestId.set(null);
    this.visible.set(true);
  }

  dismiss(): void {
    this.visible.set(false);
    this.requestId.set(null);
  }

  private categorize(error: unknown): ErrorType {
    if (!(error instanceof HttpErrorResponse)) return 'unknown';
    if (error.status === 0) return 'network';
    if (error.status === 401) return 'unauthorized';
    if (error.status === 403) return 'forbidden';
    if (error.status === 404) return 'not-found';
    if (error.status === 400) return 'bad-request';
    if (error.status >= 500) return 'server-error';
    return 'unknown';
  }
}
